import { Component, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PaymentService } from 'app/core/http/services/payment.service';
import { DialogData } from 'app/core/interfaces/dialog';
import { Payment } from 'app/core/interfaces/payment';
import { getPaymentStatusDescription, getPaymentStatusText } from 'app/core/utils/payment-status';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'src-payment-encounter',
  templateUrl: './payment-encounter.component.html',
  styleUrls: ['./payment-encounter.component.scss'],
})
export class PaymentEncounterComponent extends BaseComponent implements OnInit, OnDestroy {
  public exibitionMode: string = '';
  public paymentData!: Payment;
  public encounter: any;
  public loadingPayment = false;
  public errorMessage = '';
  public forceNewPayment = false;
  public checkingPaymentStatus = false;
  public paymentLookupFailed = false;
  private paymentStatusRefreshInFlight = false;
  private paymentStatusPolling?: Subscription;
  private expiredPendingRefreshAttempted = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private paymentService: PaymentService,
    private injector: Injector,
  ) {
    super(injector);
  }

  public qrData = '';

  copiarPix() {
    const pixCode = this.getPixCode();

    if (!pixCode) {
      this.errorMessage = 'Codigo PIX nao encontrado para este pagamento.';
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = pixCode;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    alert('Código PIX copiado com sucesso');
  }

  ngOnInit(): void {
    let parameters = this.data.param;
    if (parameters.encounter) {
      this.encounter = parameters.encounter;
    }

    this.getPaymentByEncounter();
  }

  ngOnDestroy(): void {
    this.stopPaymentStatusPolling();
  }

  get paymentMethod() {
    return this.getEncounterPaymentMethod();
  }

  getEncounterPaymentMethod(): 'PIX' | 'CREDIT_CARD' | 'PHYSICAL' {
    const paymentMethod = this.encounter?.paymentMethod;

    if (paymentMethod === 'PIX' || paymentMethod === 'CREDIT_CARD' || paymentMethod === 'PHYSICAL') {
      return paymentMethod;
    }

    return 'PHYSICAL';
  }

  getPaymentType(type: string) {
    if (type == 'CREDIT_CARD') {
      return 'Cartão de Crédito';
    }

    if (type == 'PIX') {
      return 'Pix';
    }

    return 'Presencialmente';
  }

  getStatusPayment(status: string) {
    return getPaymentStatusText(status);
  }

  getPaymentStatusHelp(): string {
    if (this.isPendingExpiredPayment(this.paymentData)) {
      return 'QR Code vencido localmente. Atualizando status do pagamento...';
    }

    return getPaymentStatusDescription(this.paymentData?.status);
  }

  getPaymentByEncounter() {
    this.loadingPayment = true;
    this.errorMessage = '';
    this.paymentLookupFailed = false;
    this.expiredPendingRefreshAttempted = false;
    this.paymentService.listByEncounter(this.encounter.encounter_id).subscribe({
      next: (payment: Payment) => {
        this.loadingPayment = false;
        this.errorMessage = '';
        this.applyPaymentState(payment, true);
      },
      error: (err) => {
        this.loadingPayment = false;
        this.forceNewPayment = false;
        this.stopPaymentStatusPolling();
        if (Number(err?.status || err?.statusCode) === 404) {
          this.exibitionMode = 'CREATE';
          return;
        }

        this.paymentLookupFailed = true;
        this.exibitionMode = '';
        this.errorMessage = 'Não foi possível consultar o pagamento deste atendimento. Tente novamente.';
      },
    });
  }

  retryPaymentLookup(): void {
    this.getPaymentByEncounter();
  }

  onConfirmPayment(payment: Payment) {
    this.errorMessage = '';
    this.applyPaymentState(payment, false);
  }

  retryPayment() {
    if (!this.canRetryPayment()) {
      return;
    }

    this.prepareNewPaymentFromExisting();
  }

  prepareNewPaymentFromExisting() {
    this.errorMessage = '';
    this.qrData = '';
    this.forceNewPayment = true;
    this.exibitionMode = 'CREATE';
  }

  isReusablePayment(payment: Payment | undefined): boolean {
    if (!payment) {
      return false;
    }

    if (payment.billingType != 'PIX') {
      return payment.status == 'PENDING' || payment.status == 'CREATING';
    }

    return (payment.status == 'PENDING' || payment.status == 'CREATING') && !this.isPaymentExpired(payment) && !!this.getPixCodeFromPayment(payment);
  }

  isPaymentExpired(payment: Payment): boolean {
    if (payment.status == 'EXPIRED') {
      return true;
    }

    const expirationValue = payment.expires_at || payment.due_at || payment.qrcode?.expiration;
    if (!expirationValue) {
      return false;
    }

    const expirationDate = new Date(expirationValue);
    if (Number.isNaN(expirationDate.getTime())) {
      return false;
    }

    return expirationDate.getTime() <= Date.now();
  }

  private applyPaymentState(payment: Payment, prepareCreateWhenNotReusable: boolean) {
    this.paymentData = payment;
    this.updateEncounterPaymentStatus(payment);

    if (this.isPendingExpiredPayment(this.paymentData)) {
      this.stopPaymentStatusPolling();
      this.forceNewPayment = false;
      this.qrData = '';
      this.exibitionMode = 'VIEW';

      if (prepareCreateWhenNotReusable && !this.expiredPendingRefreshAttempted) {
        this.expiredPendingRefreshAttempted = true;
        this.refreshPaymentStatus(true);
      }

      return;
    }

    this.expiredPendingRefreshAttempted = false;

    if (this.isReusablePayment(this.paymentData)) {
      this.forceNewPayment = false;
      this.qrData = this.getPixCode();
      this.exibitionMode = 'VIEW';
      this.startPaymentStatusPolling();
      return;
    }

    this.stopPaymentStatusPolling();

    if (this.paymentData?.status == 'PAID') {
      this.forceNewPayment = false;
      this.qrData = '';
      this.exibitionMode = 'VIEW';
      return;
    }

    if (prepareCreateWhenNotReusable && this.canGenerateReplacementPayment(this.paymentData)) {
      this.prepareNewPaymentFromExisting();
      return;
    }

    if (this.paymentData?.billingType == 'PIX') {
      this.qrData = this.getPixCode();
    }

    this.forceNewPayment = false;
    this.exibitionMode = this.paymentData ? 'VIEW' : 'CREATE';
  }

  private startPaymentStatusPolling() {
    if (this.paymentStatusPolling && !this.paymentStatusPolling.closed) {
      return;
    }

    this.checkingPaymentStatus = true;
    this.paymentStatusPolling = interval(5000).subscribe(() => this.refreshPaymentStatus());
  }

  private refreshPaymentStatus(force = false) {
    if (!force && !this.shouldPollPaymentStatus()) {
      this.stopPaymentStatusPolling();
      return;
    }

    if (this.paymentStatusRefreshInFlight) {
      return;
    }

    this.checkingPaymentStatus = true;
    this.paymentStatusRefreshInFlight = true;
    this.paymentService.listByEncounter(this.encounter.encounter_id).subscribe({
      next: (payment: Payment) => {
        this.paymentStatusRefreshInFlight = false;
        this.errorMessage = '';
        this.applyPaymentState(payment, true);
      },
      error: () => {
        this.paymentStatusRefreshInFlight = false;
        this.checkingPaymentStatus = false;
        this.errorMessage = 'Não foi possível atualizar o status do pagamento. Tentaremos novamente.';
      },
    });
  }

  private stopPaymentStatusPolling() {
    if (this.paymentStatusPolling) {
      this.paymentStatusPolling.unsubscribe();
      this.paymentStatusPolling = undefined;
    }
    this.checkingPaymentStatus = false;
    this.paymentStatusRefreshInFlight = false;
  }

  private shouldPollPaymentStatus(): boolean {
    return (
      this.exibitionMode == 'VIEW' &&
      (this.paymentData?.billingType == 'PIX' || this.paymentData?.billingType == 'CREDIT_CARD') &&
      (this.paymentData?.status == 'PENDING' || this.paymentData?.status == 'CREATING') &&
      !!this.encounter?.encounter_id
    );
  }

  private updateEncounterPaymentStatus(payment: Payment) {
    if (!this.encounter || !payment?.status) {
      return;
    }

    this.encounter.status_payment = payment.status;

    if (payment.confirmed_at || payment.payment_at) {
      this.encounter.payment_at = payment.confirmed_at || payment.payment_at;
    }
  }

  canRetryPayment(): boolean {
    return this.exibitionMode == 'VIEW' && this.canGenerateReplacementPayment(this.paymentData);
  }

  shouldShowPixQrCode(): boolean {
    return (
      this.exibitionMode == 'VIEW' &&
      this.paymentData?.billingType == 'PIX' &&
      this.paymentData?.status != 'PAID' &&
      !this.isPaymentExpired(this.paymentData) &&
      !!this.qrData
    );
  }

  private isPendingExpiredPayment(payment: Payment | undefined): boolean {
    return (
      !!payment && payment.billingType == 'PIX' && (payment.status == 'PENDING' || payment.status == 'CREATING') && this.isPaymentExpired(payment)
    );
  }

  isTerminalPayment(status: string): boolean {
    return ['FAILED', 'EXPIRED', 'REFUNDED', 'CANCELED'].includes(status);
  }

  canGenerateReplacementPayment(payment: Payment | undefined): boolean {
    const retryableStatus = payment?.billingType === 'PIX' ? ['FAILED', 'EXPIRED'] : ['FAILED', 'CANCELED'];
    return (payment?.billingType === 'PIX' || payment?.billingType === 'CREDIT_CARD') && retryableStatus.includes(payment.status || '');
  }

  getColorStatus(status: string) {
    if (status == 'PAID') {
      return 'pago';
    }
    if (status == 'NOT_PAID' || status == 'PENDING' || status == 'CREATING') {
      return 'pendente';
    }
    if (status == 'FAILED' || status == 'EXPIRED' || status == 'REFUNDED' || status == 'CANCELED') {
      return 'falhou';
    }
    return '';
  }

  getPixCode(): string {
    return this.getPixCodeFromPayment(this.paymentData);
  }

  private getPixCodeFromPayment(payment: Payment | undefined): string {
    return payment?.pix_qr_code || payment?.qrcode?.code || '';
  }

  formatPaymentValue(value: string | number | undefined): string {
    const cents = this.parsePaymentCents(value);

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(cents / 100);
  }

  private parsePaymentCents(value: string | number | undefined): number {
    if (value === undefined || value === null || value === '') {
      return 0;
    }

    const rawValue = String(value).trim();

    if (rawValue.includes(',')) {
      const reais = Number(rawValue.replace(/\./g, '').replace(',', '.'));
      return Number.isFinite(reais) ? Math.round(reais * 100) : 0;
    }

    const cents = Number(rawValue);
    return Number.isFinite(cents) ? Math.round(cents) : 0;
  }
}
